import ThreeCPlusRepository from '@/shared/http/repositories/configurations/threeCPlus'

/**
 * Configurações da integração 3C Plus
 * Agora com suporte a configurações dinâmicas
 */
class ThreeCConfig {
  constructor () {
    this.config = null
    this.userConfig = null
    this.isLoaded = false
  }

  /**
   * Carrega as configurações do servidor
   */
  async loadConfig () {
    try {
      const response = await ThreeCPlusRepository.getConfig()

      if (response.status === 200) {
        this.config = response.data
        this.isLoaded = true
        console.log('✅ Configuração 3C Plus carregada do servidor:', {
          domain: this.config.domain,
          socketUrl: this.config.socketUrl,
          enabled: this.config.enabled
        })
        return this.config
      } else if (response.status === 404) {
        console.warn('⚠️ Configuração 3C Plus não encontrada (404), telefone desabilitado')
        this.config = null
        this.isLoaded = false
        return null
      } else {
        console.warn('⚠️ Resposta inválida do servidor, telefone desabilitado')
        this.config = null
        this.isLoaded = false
        return null
      }
    } catch (error) {
      // Se for erro 404, trata como não configurado
      if (error.response && error.response.status === 404) {
        console.warn('⚠️ Configuração 3C Plus não encontrada (404), telefone desabilitado')
        this.config = null
        this.isLoaded = false
      } else {
        console.warn('⚠️ Erro ao carregar configuração do servidor, telefone desabilitado:', error.message)
        this.config = null
        this.isLoaded = false
      }
      return null
    }
  }

  /**
   * Carrega as configurações do usuário atual
   */
  async loadUserConfig (systemUserId) {
    try {
      console.log('👤 Carregando configuração do usuário 3C Plus...')

      // Usar o novo endpoint para buscar credenciais do usuário logado
      const response = await ThreeCPlusRepository.getCurrentUserCredentials()

      if (response.status === 200) {
        this.userConfig = response.data
        console.log('✅ Configuração do usuário 3C Plus carregada:', {
          username: this.userConfig.threecUsername,
          password: this.userConfig.threecPassword ? '***' : 'VAZIO',
          enabled: this.userConfig.enabled
        })
        return this.userConfig
      } else if (response.status === 404) {
        console.warn('⚠️ Usuário não possui credenciais 3C Plus cadastradas (404)')
        this.userConfig = null
        return null
      } else {
        console.warn('⚠️ Resposta inválida ao buscar credenciais 3C Plus')
        this.userConfig = null
        return null
      }
    } catch (error) {
      // Trata especificamente o erro 404
      if (error.response && error.response.status === 404) {
        console.warn('⚠️ Usuário não possui credenciais 3C Plus cadastradas (404)')
        this.userConfig = null
        return null
      }

      console.warn('⚠️ Erro ao carregar credenciais do usuário 3C Plus:', error.message)
      this.userConfig = null
      return null
    }
  }

  /**
   * Verifica se a integração está habilitada
   */
  get isEnabled () {
    try {
      if (!this.isLoaded || !this.config) {
        return false
      }
      const result = this.config.enabled === true
      return result
    } catch (error) {
      return false
    }
  }

  /**
   * Verifica se o usuário atual tem 3C Plus habilitado
   */
  get isUserEnabled () {
    try {
      const result = this.userConfig && this.userConfig.enabled === true
      return result
    } catch (error) {
      return false
    }
  }

  /**
   * Retorna o domínio configurado
   */
  get DOMAIN () {
    if (!this.config || !this.config.domain) {
      console.warn('⚠️ Tentando acessar DOMAIN sem configuração carregada')
      return null
    }
    return this.config.domain
  }

  /**
   * Retorna a URL base da API
   */
  get API_BASE_URL () {
    if (!this.DOMAIN) {
      return null
    }
    return `https://${this.DOMAIN}/api/v1`
  }

  /**
   * Retorna a URL do socket
   */
  get SOCKET_URL () {
    if (!this.config || !this.config.socketUrl) {
      console.warn('⚠️ Tentando acessar SOCKET_URL sem configuração carregada')
      return null
    }
    return this.config.socketUrl
  }

  /**
   * Retorna o timeout de conexão
   */
  get CONNECTION_TIMEOUT () {
    if (!this.config) {
      return 15000 // Timeout padrão apenas
    }
    return this.config.connectionTimeout || 15000
  }

  /**
   * Retorna o timeout do iframe
   */
  get IFRAME_LOAD_TIMEOUT () {
    if (!this.config) {
      return 10000 // Timeout padrão apenas
    }
    return this.config.iframeTimeout || 10000
  }

  /**
   * Retorna as opções do socket
   */
  get SOCKET_OPTIONS () {
    return {
      transports: ['websocket'],
      timeout: 10000
    }
  }

  /**
   * Retorna as credenciais do usuário atual
   */
  get USER () {
    if (!this.userConfig || !this.userConfig.threecUsername) {
      console.warn('⚠️ Tentando acessar USER sem credenciais carregadas')
      return null
    }
    return this.userConfig.threecUsername
  }

  /**
   * Retorna a senha do usuário atual
   */
  get PASSWORD () {
    if (!this.userConfig || !this.userConfig.threecPassword) {
      console.warn('⚠️ Tentando acessar PASSWORD sem credenciais carregadas')
      return null
    }
    return this.userConfig.threecPassword
  }

  /**
   * Inicializa as configurações
   */
  async init (userId = null) {
    try {
      this.isLoaded = false
      this.config = null
      this.userConfig = null

      const config = await this.loadConfig() // Carrega configuração do servidor

      // Se não houver configuração no backend, não continua
      if (!config) {
        console.warn('⚠️ Configuração 3C Plus não disponível, serviço desabilitado')
        return false
      }

      // Carregar configuração do usuário ANTES de marcar como carregado
      if (userId) {
        await this.loadUserConfig(userId)
      }

      this.isLoaded = true // Agora sim, marcar como carregado
      return true
    } catch (error) {
      console.warn('❌ Erro ao inicializar configuração 3C Plus:', error.message)
      this.config = null
      this.userConfig = null
      this.isLoaded = false
      return false
    }
  }

  /**
   * Recarrega as configurações
   */
  async reload (userId = null) {
    this.isLoaded = false
    this.config = null
    this.userConfig = null
    await this.init(userId)
  }
}

const threeCConfig = new ThreeCConfig()

export { ThreeCConfig }
export default threeCConfig



// WEBPACK FOOTER //
// ./src/shared/services/voip/ThreeCConfig.js